/**
 * useDadosDeCobranca — lógica da modal "Dados para o pagamento" (pacote e domínio).
 * Planos: docs/PLANO-DOCUMENTO-CPF-CNPJ-E-COBRANCA.md (dados) e docs/PLANO-CARTAO-PACOTES.md (meio).
 *
 * Uso:
 *   const cobranca = useDadosDeCobranca();
 *   cobranca.abrir(async ({ document, billingName, method }) => {
 *     try { ...compra...; return { ok: true }; }
 *     catch (e) { return { ok: false, erro: 'mensagem' }; }   // ou { ok: false, fechar: true }
 *   }, { obterMetodos: () => billingService.getPaymentMethods('pack') });   // sem isto: só PIX
 *   <DadosDeCobrancaDialog vm={cobranca.vm} actions={cobranca.actions} />
 *
 * Ciclo:
 *  - abrir: busca EM PARALELO os dados salvos (GET /users/documents — sugestão já resolvida pela API:
 *    cobrança salva → senão conta) e os meios liberados. Falhou um deles → em branco / só PIX; nunca
 *    trava a compra.
 *  - confirmar: valida no front (dígito verificador, nome) e só então chama a compra com o documento
 *    LIMPO e o meio escolhido. Quem grava a cobrança é a API, DEPOIS que o pedido existe.
 */
import { useState, useCallback, useRef } from 'react';
import usersService from '../services/usersService';
import { mascararDocumento, validarDocumento } from '../utils/documento';
import {
  DADOS_DE_COBRANCA_FECHADO as FECHADO,
  MEIOS_DE_PAGAMENTO,
} from '../components/billing/dadosDeCobranca.vm';

const SO_PIX = ['Pix'];

/** Ids vindos da API → opções do radio. Desconhecido sai; vazio vira só PIX. */
function montarMetodos(ids) {
  const conhecidos = (Array.isArray(ids) ? ids : [])
    .filter((id, i, todos) => MEIOS_DE_PAGAMENTO[id] && todos.indexOf(id) === i);
  return (conhecidos.length ? conhecidos : SO_PIX).map((id) => ({ id, label: MEIOS_DE_PAGAMENTO[id].label }));
}

/** Texto acima do botão e rótulo do botão acompanham o meio escolhido. */
function comMetodo(estado, method) {
  const meio = MEIOS_DE_PAGAMENTO[method] ?? MEIOS_DE_PAGAMENTO.Pix;
  return { ...estado, method, methodHint: meio.hint, confirmLabel: meio.confirmLabel };
}

export default function useDadosDeCobranca() {
  const [estado, setEstado] = useState(FECHADO);
  const aoConfirmar = useRef(null);
  // Cada abertura tem um número: a resposta de uma busca antiga (abriu, fechou, abriu de novo)
  // não pode preencher a modal da abertura nova.
  const abertura = useRef(0);

  const abrir = useCallback(async (onConfirm, { obterMetodos } = {}) => {
    const minha = ++abertura.current;
    aoConfirmar.current = onConfirm;
    setEstado({ ...FECHADO, open: true, loading: true });

    const [dados, ids] = await Promise.all([
      usersService.getDocuments().catch(() => null),          // sem dados salvos a pessoa digita
      obterMetodos ? obterMetodos().catch(() => SO_PIX) : Promise.resolve(SO_PIX),
    ]);
    if (minha !== abertura.current) return;

    const methods = montarMetodos(ids);
    const padrao = methods.some((m) => m.id === 'Pix') ? 'Pix' : methods[0].id;
    setEstado((e) => (e.open
      ? comMetodo({
        ...e,
        loading: false,
        name: dados?.billingSuggestion?.billingName ?? '',
        document: mascararDocumento(dados?.billingSuggestion?.billingDocument ?? ''),
        methods,
      }, padrao)
      : e));
  }, []);

  const onChangeName = useCallback((valor) => {
    setEstado((e) => ({ ...e, name: valor, nameError: null, formError: null }));
  }, []);

  const onChangeDocument = useCallback((valor) => {
    setEstado((e) => ({ ...e, document: mascararDocumento(valor), documentError: null, formError: null }));
  }, []);

  const onChangeMethod = useCallback((id) => {
    setEstado((e) => (e.busy || !e.methods.some((m) => m.id === id) ? e : comMetodo({ ...e, formError: null }, id)));
  }, []);

  const onCancel = useCallback(() => {
    setEstado((e) => (e.busy ? e : FECHADO));
  }, []);

  const onConfirm = useCallback(async () => {
    const atual = estado;
    if (atual.busy || atual.loading) return;

    const documento = validarDocumento(atual.document);
    const nome = (atual.name || '').trim();
    const nameError = nome ? null : 'Informe o nome ou a razão social.';
    if (!documento.valido || nameError) {
      setEstado((e) => ({ ...e, documentError: documento.erro, nameError }));
      return;
    }

    setEstado((e) => ({ ...e, busy: true, formError: null }));
    let resultado;
    try {
      resultado = await aoConfirmar.current?.({ document: documento.limpo, billingName: nome, method: atual.method });
    } catch {
      resultado = { ok: false, erro: 'Não foi possível iniciar o pagamento. Tente novamente.' };
    }

    if (resultado?.ok || resultado?.fechar) {
      setEstado(FECHADO);
      return;
    }
    setEstado((e) => ({
      ...e,
      busy: false,
      formError: resultado?.erro || 'Não foi possível iniciar o pagamento. Tente novamente.',
    }));
  }, [estado]);

  return {
    abrir,
    vm: estado,
    actions: { onChangeName, onChangeDocument, onChangeMethod, onConfirm, onCancel },
  };
}
